import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import {
  ActionListItem,
  AlertDialog,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
  Illustration,
} from '@node-space/ui-components'
import { wait } from '@node-space/utils'
import { useDeleteNotificationDestinationMutation } from 'hooks/mutations/useDeleteNotificationDestinationMutation'
import { useToastContext } from 'hooks/useToastContext'
import { NotificationDestination } from '../types'

interface DestinationItemProps {
  destination: NotificationDestination
  index: number
  activeRowId: string
  onDetailsClick: (index: number) => void
}

export const DestinationItem = ({
  destination,
  index,
  activeRowId,
  onDetailsClick,
}: DestinationItemProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const addToast = useToastContext()
  const [_, setCopied] = useState<string | null>()
  const [copyText, setCopyText] = useState<string>(t('copy'))
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false)
  const [showCopiedTag, setShowCopiedTag] = useState(false)

  const handleOpen = () => {
    setIsAlertDialogOpen(true)
  }

  const handleClose = () => {
    setIsAlertDialogOpen(false)
  }

  const { mutate: deleteWebHookDestination, isPending: isDeleteWebHookDestinationLoading } =
    useDeleteNotificationDestinationMutation()

  const onDeleteItem = destinationId => {
    const payload = {
      destinationId,
    }
    deleteWebHookDestination(payload, {
      onSuccess: () => {
        handleClose()
        queryClient.invalidateQueries({ queryKey: ['destinations'] })
        addToast({
          title: t('integrations.removeDestination'),
          state: 'success',
          delay: 0,
        })
      },
      onError: error => {
        addToast({
          title: t('oopsSomethingWentWrong'),
          state: 'error',
          delay: 10,
        })
      },
    })
  }

  const onCopy = async (value: string) => {
    setCopied(value)
    setShowCopiedTag(true)
    await wait(2000)
    setShowCopiedTag(false)
    setCopyText(t('copy'))
  }

  const activeRow = clsx('cursor-pointer hover:bg-lighterBlueTint', {
    'bg-secondary-50': activeRowId && activeRowId?.toString() === destination?.id,
  })

  return (
    <Box key={destination?.id} className={activeRow}>
      <ActionListItem
        id={destination?.id}
        border={false}
        title={destination?.description || destination?.url}
        Avatar={<Icon name="Globe" />}
        CustomAction={
          <DropdownMenu
            DropdownMenuItems={[
              <DropdownMenuItem
                key={`action-info-${destination?.id}`}
                LeadingIcon={<Icon name="Info" />}
                onClick={() => onDetailsClick(index)}
              >
                {t('integrations.details')}
              </DropdownMenuItem>,
              <DropdownMenuItem
                key={`action-delete-${destination?.id}`}
                LeadingIcon={<Icon name="Trash" />}
                onClick={() => handleOpen()}
              >
                {t('integrations.delete')}
              </DropdownMenuItem>,
            ]}
          >
            <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
          </DropdownMenu>
        }
        ActionButton={
          showCopiedTag ? (
            <>
              <Highlight variant="emphasis" type="success" iconElement={<Icon name="Check" />}>
                {t('integrations.urlCopied')}
              </Highlight>
            </>
          ) : (
            <CopyToClipboard text={destination?.url} onCopy={() => onCopy(destination?.url)}>
              <Button iconElement={<Icon size="small" name="Copy" />} variant="ghost">
                {t('integrations.copyURL')}
              </Button>
            </CopyToClipboard>
          )
        }
      />
      <AlertDialog
        open={isAlertDialogOpen}
        onClose={handleClose}
        title={t('integrations.alertDialogTitle', {
          url: destination?.url,
          interpolation: { escapeValue: false },
        })}
        IconElement={<Illustration name="CloudLightning" />}
        ActionButton={
          <Button
            onClick={() => onDeleteItem(destination?.id)}
            variant="destructive"
            iconElement={<Icon name="Trash" />}
            loading={isDeleteWebHookDestinationLoading}
          >
            {t('integrations.delete')}
          </Button>
        }
      >
        {t('integrations.alertDialogDescription')}
      </AlertDialog>
    </Box>
  )
}
